import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Card, List, NavBar, Toast, Dialog} from 'antd-mobile';
import {ShopbagOutline, LockOutline} from 'antd-mobile-icons';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import HereMapView from '../../delivery/delivering/components/HereMapView';
import PickupPhoneAndNavigate from '../../pickup/components/PickupPhoneAndNavigate';
import {buildAddress} from "../../../utils/addressUtils";
import {formatTimestampByLanguage} from "../../../utils/dateTimeUtils";
import {useTranslation} from "react-i18next";
import {useTmsTaskWaypoint, useTransportStore} from "../store/transportStore";
import {TmsWaypointWithAddressType} from "../graphql/queries/getTmsPublishedTaskByDriverAccountId";
import {t} from "../../../i18n";
import {copyToClipboard} from "../../../utils/copyToClipboard";
import TransportInfoItem from "../components/TransportInfoItem";
import {TmsPath, TmsPathPre} from "../../../enums/TmsNavigatePathEnum";
import MapboxDetailView from "../../delivery/delivering/components/MapboxDetailView";
import SwipeToDepartButton from "./components/SwipeToDepartButton";
import { useTmsTaskOperations } from "../store/transportStore";
import { useHistoryControl } from "../../../hooks/useHistoryControl";
import { TmsOperationSourceEnum } from "../../../enums/TmsEnum";
import { translateResultCode } from "../../../utils/resultCode";

const TransportDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { taskWayPointId, waypointType } = useParams<{ taskWayPointId: string; waypointType: string }>();
    const startOrEndFlag = waypointType === '1' || waypointType === '3';
    const { i18n } = useTranslation();

    // 使用 useHistoryControl 统一控制返回行为，让右滑返回也跳转到列表页
    const { handleBackClick } = useHistoryControl(TmsPath.TMS_TRANSPORTING, {
        useReplace: false,
        controlNavButton: true
    });

    const { tmsTaskWaypoint, loading, refreshTmsTaskWaypointList } = useTmsTaskWaypoint(taskWayPointId || '');
    // 直接从 store 读取列表，不启动轮询（轮询由列表页管理）
    const tmsTaskWaypointList = useTransportStore(state => state.tmsTaskWaypointList);
    const { fetchTmsTaskWaypointById, updateTmsTaskWaypointToDeparture } = useTmsTaskOperations();
    const getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId = useTransportStore(state => state.getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId);
    const [waypointDetail, setWaypointDetail] = useState<TmsWaypointWithAddressType>();
    const [triedRefresh, setTriedRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [parcelCount, setParcelCount] = useState<number>(0);

    // 判断上一个节点的状态是否为 DONE（必须在所有条件返回之前调用）
    const isPreviousWaypointDone = useMemo(() => {
        if (!tmsTaskWaypoint || !tmsTaskWaypointList || tmsTaskWaypointList.length === 0) {
            return true; // 如果没有数据，默认允许操作
        }
        // 按 sequence 排序找到当前节点
        const sortedWaypoints = [...tmsTaskWaypointList].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        const currentIndex = sortedWaypoints.findIndex(
            wp => wp.taskWayPointId === tmsTaskWaypoint.taskWayPointId
        );
        // 如果是第一个节点，没有上一个节点，允许操作
        if (currentIndex <= 0) {
            return true;
        }
        // 获取上一个节点
        const previousWaypoint = sortedWaypoints[currentIndex - 1];
        // 判断上一个节点的状态是否为 DONE
        return previousWaypoint?.status === 'DONE';
    }, [tmsTaskWaypoint, tmsTaskWaypointList]);

    // 获取下一个未完成的节点
    const nextWaypoint = useMemo(() => {
        if (!tmsTaskWaypoint || !tmsTaskWaypointList || tmsTaskWaypointList.length === 0) {
            return null;
        }
        // 按 sequence 排序找到当前节点
        const sortedWaypoints = [...tmsTaskWaypointList].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        const currentIndex = sortedWaypoints.findIndex(
            wp => wp.taskWayPointId === tmsTaskWaypoint.taskWayPointId
        );
        // 从当前节点后面找到第一个未完成的节点
        if (currentIndex >= 0 && currentIndex < sortedWaypoints.length - 1) {
            for (let i = currentIndex + 1; i < sortedWaypoints.length; i++) {
                const wp = sortedWaypoints[i];
                if (wp.status !== 'DONE') {
                    return wp;
                }
            }
        }
        return null;
    }, [tmsTaskWaypoint, tmsTaskWaypointList]);

    // 复制地址功能
    const handleCopyAddress = async () => {
        if (!address || address.trim() === '') {
            Toast.show({
                content: t('pages.tms.transport.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(address);
            if (success) {
                Toast.show({
                    content: t('pages.tms.transporting.addressCopied'),
                    position: 'bottom'
                });
            } else {
                Toast.show({
                    content: t('pages.tms.transporting.copyFailed'),
                    position: 'bottom'
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.tms.transporting.copyFailed'),
                position: 'bottom'
            });
        }
    };

    useEffect(() => {
        if (tmsTaskWaypoint){
            setWaypointDetail(tmsTaskWaypoint);
        }

    }, [tmsTaskWaypoint]);
    useEffect(() => {
        if (!tmsTaskWaypoint && !loading && !triedRefresh) {
            setTriedRefresh(true);
            refreshTmsTaskWaypointList();
        }
    }, [tmsTaskWaypoint, loading, triedRefresh, refreshTmsTaskWaypointList]);

    // 处理到达站点
    const handleWayPointArrived = () => {
        // 导航到详情页
        navigate(TmsPathPre.TMS_TRANSPORTING_ARRIVING_PRE + `${waypointDetail?.taskWayPointId}`);
    };

    // 处理滑动发车
    const handleSwipeToDepart = async () => {
        try {
            if (!taskWayPointId) {
                return;
            }
            const waypoint = await fetchTmsTaskWaypointById(taskWayPointId);
            const sealTime = waypoint?.sealTime?.trim();
            const sealNumber = waypoint?.sealNumber?.trim();
            const checkSealNumberFlag = waypoint?.checkSealNumberFlag ?? false;
            // 只有当 checkSealNumberFlag 为 true 时才校验 sealTime
            if (checkSealNumberFlag && !sealTime) {
                Dialog.alert({
                    title: (
                        <div
                            className="flex items-center"
                            style={{ gap: 8, width: '100%', justifyContent: 'center' }}
                        >
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    backgroundColor: '#FFA500',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    lineHeight: '20px'
                                }}
                            >
                                !
                            </span>
                            <span>{t('pages.tms.transporting.notSealedNoDepart.title')}</span>
                        </div>
                    ),
                    content: (
                        <div className="text-sm leading-relaxed">
                            <span>{t('pages.tms.transporting.notSealedNoDepart.part1')}</span>
                            <span className="text-red-600">{t('pages.tms.transporting.notSealedNoDepart.part2Red')}</span>
                            <span>{t('pages.tms.transporting.notSealedNoDepart.part3')}</span>
                            <span className="text-red-600">{t('pages.tms.transporting.notSealedNoDepart.part4Red')}</span>
                            <span>{t('pages.tms.transporting.notSealedNoDepart.part5')}</span>
                        </div>
                    ),
                    confirmText: t('pages.tms.transporting.known'),
                    // 设置主题主色为紫色，使确认按钮呈紫色
                    style: ({ '--adm-color-primary': '#6f42c1' } as React.CSSProperties),
                });
                return;
            }
            // 查询包裹数量（在显示弹窗之前完成查询）
            let currentParcelCount = 0;
            if (taskWayPointId && waypoint?.taskId && waypoint?.id) {
                currentParcelCount = await getLoadingOrderDetailWaypointCountByTaskIdAndWaypointId(waypoint.taskId, Number(waypoint.id));
                setParcelCount(currentParcelCount);
            }
            //发车确认弹窗
            const handler = Dialog.show({
                title: t('pages.tms.transporting.confirmDeparture.title'),
                content: (
                    <div className="flex flex-col items-center">
                        <p className="text-gray-500 text-sm mb-3 text-center">{t('pages.tms.transporting.confirmDeparture.desc')}</p>
                        <div className="flex items-center justify-between mb-4 w-full px-4">
                            <div className="flex items-center gap-2">
                                <ShopbagOutline fontSize={20} style={{ color: '#6b7280' }} />
                                <span className="text-black text-sm font-semibold">{t('pages.tms.transporting.quantity')}:</span>
                            </div>
                            <span className="text-purple-700 font-semibold">{currentParcelCount}</span>
                        </div>
                        <div className="flex items-start justify-between mb-4 w-full px-4 gap-2">
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <LockOutline fontSize={20} style={{ color: '#6b7280' }} />
                                <span className="text-black text-sm font-semibold">{t('pages.tms.transporting.sealNumber')}:</span>
                            </div>
                            <span className="text-purple-700 font-semibold break-words text-right flex-1 min-w-0">{sealNumber || '-'}</span>
                        </div>
                        <Button
                            color="primary"
                            block
                            onClick={async () => {
                                if (waypoint?.taskId && taskWayPointId) {
                                    const result = await updateTmsTaskWaypointToDeparture({
                                        tmsTaskWayPointId: parseInt(taskWayPointId),
                                        taskId: waypoint.taskId,
                                        operationSource: TmsOperationSourceEnum.APP,
                                    });
                                    handler.close();
                                    if (result.success) {
                                        // 刷新列表数据
                                        await refreshTmsTaskWaypointList();
                                        // 跳转到下一个节点或返回列表页
                                        if (nextWaypoint) {
                                            navigate(`${TmsPathPre.TMS_TRANSPORTING_DETAIL_PRE}${nextWaypoint.taskWayPointId}/${nextWaypoint.waypointType || '2'}`);
                                        } else {
                                            navigate(-1);
                                        }
                                    } else {
                                        const errorMsg = translateResultCode(result.code, result.message);
                                        if (errorMsg) {
                                            Toast.show({ content: errorMsg, position: 'center' });
                                        }
                                    }
                                } else {
                                    handler.close();
                                }
                            }}
                        >
                            {t('pages.tms.transporting.confirmDeparture.confirm')}
                        </Button>
                        <button
                            type="button"
                            className="mt-2 text-gray-500 text-sm text-center"
                            onClick={() => handler.close()}
                        >
                            {t('pages.tms.transporting.confirmDeparture.contact')}
                        </button>
                    </div>
                ),
                style: ({ '--adm-color-primary': '#6f42c1' } as React.CSSProperties),
            });
        } catch (e) {
            console.error('handleSwipeToDepart error:', e);
        }
    };

    const handleBack = () => {
        //跳转到列表页面
        navigate(TmsPath.TMS_TRANSPORTING);
    };

    if (loading|| !waypointDetail) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        Loading
                    </NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <p>Loading</p>
                </div>
            </div>
        );
    }
    if (!tmsTaskWaypoint) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        Not Found
                    </NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <p>No waypoint data</p>
                </div>
            </div>
        );
    }

    const address = buildAddress(
        tmsTaskWaypoint.addressLine1 ?? undefined,
        tmsTaskWaypoint.addressLine2 ?? undefined,
        tmsTaskWaypoint.city ?? undefined,
        tmsTaskWaypoint.state ?? undefined,
        tmsTaskWaypoint.zipcode ?? undefined,
        tmsTaskWaypoint.country ?? undefined
    );

    const stopNumber = tmsTaskWaypoint.sequence;
    const title = tmsTaskWaypoint.wayPointName || 'Waypoint';

    return (
        <div className="flex flex-col h-screen">
            {/* Navbar */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>
                    {title}
                </NavBar>
            </div>

            {/* Map */}
            <div className="h-2/5">
                <MapboxDetailView stopNumber={
                    waypointType === '1' ? t('pages.tms.transporting.start') :
                    waypointType === '3' ? t('pages.tms.transporting.end') :
                        tmsTaskWaypoint.sequence
                } address={address}/>
            </div>

            {/* Waypoint Details */}
            <Card className="flex-1 m-2 rounded-lg overflow-y-auto flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex flex-col w-14 h-full justify-center items-center">
                        <p className="text-2xl font-bold" style={{color: 'var(--adm-color-primary)'}}>
                            {waypointType === '1' && t('pages.tms.start')}
                            {waypointType === '3' && t('pages.tms.end')}
                            {!startOrEndFlag && tmsTaskWaypoint?.sequence}
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="text-lg font-medium">{tmsTaskWaypoint.contactName || '-'}</h3>
                        <div className="text-sm text-gray-500">{`${tmsTaskWaypoint.contactPhone || ''}${tmsTaskWaypoint.ext ? `-${tmsTaskWaypoint.ext}` : ''}`}</div>
                        <div className="flex-1 text-base">
                            <div className="flex items-center gap-2">
                                <span>{address}</span>
                                <button
                                    onClick={handleCopyAddress}
                                    className="p-1 flex items-center justify-center text-purple-700 "
                                    style={{minWidth: '24px', height: '24px'}}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path
                                            d="M7 4V2C7 1.45 7.45 1 8 1H20C20.55 1 21 1.45 21 2V16C21 16.55 20.55 17 20 17H18V19C18 20.1 17.1 21 16 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7V4M7 7H4V19H16V7H7Z"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <PickupPhoneAndNavigate phone={tmsTaskWaypoint.contactPhone || ''} address={address}/>

                <List
                    style={{
                        '--border-top': 'none',
                        '--border-bottom': 'none',
                        '--padding-left': '0px',
                        '--padding-right': '0px',
                    } as any}
                >
                    <>
                        {tmsTaskWaypoint.planArrivalTime && (
                            <TransportInfoItem label={t('pages.tms.transporting.planArrivalTime')}
                                               value={formatTimestampByLanguage(tmsTaskWaypoint.planArrivalTime, i18n.language) + (tmsTaskWaypoint.planArrivalTimezone ? ` (${tmsTaskWaypoint.planArrivalTimezone})` : '')}/>
                        )}
                        {tmsTaskWaypoint.planDepartureTime && (
                            <TransportInfoItem label={t('pages.tms.transporting.planDepartureTime')}
                                               value={formatTimestampByLanguage(tmsTaskWaypoint.planDepartureTime, i18n.language) + (tmsTaskWaypoint.planDepartureTimezone ? ` (${tmsTaskWaypoint.planDepartureTimezone})` : '')}/>
                        )}

                    </>
                </List>
            </Card>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                {tmsTaskWaypoint.status === 'PLANNED' ? (
                    <Button color='primary'
                            onClick={handleWayPointArrived}
                            disabled={!isPreviousWaypointDone}
                            className="flex-1">
                        {t('pages.tms.transporting.iHaveArrived')}
                    </Button>
                ) : (tmsTaskWaypoint.status === 'ARRIVED' || tmsTaskWaypoint.status === 'PENDING_DEPARTURE') && waypointType !== '3' ? (
                    <SwipeToDepartButton
                        onSwipeComplete={handleSwipeToDepart}
                        text={t('pages.tms.transporting.swipeToDepart')}
                        disabled={!isPreviousWaypointDone}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    );
};

export default TransportDetailPage;


